export enum TestItemType {
    TEST_CASE = 'test_case',
    CHECKLIST = 'checklist',
    FOLDER = 'folder'
}

export enum TestItemPriority {
    LOW = 'low',
    MEDIUM = 'medium',
    HIGH = 'high',
    CRITICAL = 'critical'
}

export enum TestItemSeverity {
    LOW = 'low',
    MEDIUM = 'medium',
    HIGH = 'high',
    CRITICAL = 'critical',
    BLOCKER = 'blocker',
    MAJOR = 'major',
    MINOR = 'minor',
    TRIVIAL = 'trivial'
}

export enum TestItemStatus {
    DRAFT = 'draft',
    ACTIVE = 'active',
    DEPRECATED = 'deprecated',
    READY = 'ready',
    REVIEWED = 'reviewed',
    OBSOLETE = 'obsolete',
    COMPLETED = 'completed',
    IN_PROGRESS = 'in_progress'
}

export interface TestItemStep {
    id?: number;
    action: string;
    expected_result: string;
    test_data?: string;
    attachment_ids?: number[];
    test_data_ids?: number[];
}

export interface TestItemCheckItem {
    id?: number;
    description: string;
    is_checked?: boolean;
    attachment_ids?: number[];
    test_data_ids?: number[];
}

export interface TestItem {
    id: number;
    project_id: number;
    title: string;
    name?: string;
    description?: string;
    type: TestItemType;
    priority: TestItemPriority;
    severity: TestItemSeverity;
    status: TestItemStatus;
    folder_id?: number;
    folder?: {
        id: number;
        name: string;
    };
    estimate_minutes?: number;
    components?: string[];
    component?: string;
    labels?: string[];
    steps?: TestItemStep[];
    check_items?: TestItemCheckItem[];
    created_at: string;
    updated_at: string;
    created_by?: number;
    updated_by?: number;
    version: number;
    display_number?: string;
    sequential_number?: number;
}

export interface TestItemCreate {
    project_id: number;
    title: string;
    description?: string;
    type: TestItemType;
    priority: TestItemPriority;
    severity: TestItemSeverity;
    status: TestItemStatus;
    folder_id?: number;
    estimate_minutes?: number;
    components?: string[];
    labels?: string[];
    steps?: TestItemStep[];
    check_items?: TestItemCheckItem[];
}

export interface TestItemUpdate {
    project_id: number;
    title?: string;
    description?: string;
    type?: TestItemType;
    priority?: TestItemPriority;
    severity?: TestItemSeverity;
    status?: TestItemStatus;
    folder_id?: number;
    estimate_minutes?: number;
    components?: string[];
    labels?: string[];
    steps?: TestItemStep[];
    check_items?: TestItemCheckItem[];
}

export interface TestItemFilter {
    project_id?: number;
    type?: TestItemType;
    priority?: TestItemPriority;
    severity?: TestItemSeverity;
    status?: TestItemStatus;
    folder_id?: number;
    ids?: number[];
    search?: string;
    page?: number;
    page_size?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
}

export interface Folder {
    id: number;
    project_id: number;
    parent_id?: number;
    name: string;
    description?: string;
    created_by?: number;
    updated_by?: number;
    created_at?: string;
    updated_at?: string;
}

export interface FolderCreate {
    project_id: number;
    parent_id?: number;
    name: string;
    description?: string;
    created_by?: number;
    updated_by?: number;
}

export interface FolderUpdate {
    parent_id?: number;
    name?: string;
    description?: string;
    updated_by?: number;
}

export interface TestItemVersion {
    id: number;
    test_item_id: number;
    version: number;
    name: string;
    description: string;
    created_by_id: number;
    created_at: string;
    data: {
        title: string;
        name: string;
        description: string;
        type: string;
        priority: string;
        severity: string;
        status: string;
        components: string[];
        labels: string[];
        steps: any[];
        check_items: any[];
        folder_id: number | null;
        project_id: number;
        parent_id: number | null;
        version: number;
        id: number;
        created_by: number;
        updated_by: number | null;
        created_at: string;
        updated_at: string;
        sequential_number: number;
        project_prefix: string;
        display_number: string;
    };
}

export interface SharedStep {
    id: number;
    project_id: number;
    name: string;
    description: string;
    steps: TestItemStep[];
    created_by: number;
    created_at: string;
}

export interface TestSuite {
    id: number;
    project_id: number;
    name: string;
    description: string;
    test_item_ids: number[];
}

export interface CustomFieldDefinition {
    id: number;
    project_id: number;
    name: string;
    type: 'string' | 'number' | 'date' | 'list' | 'boolean' | 'user';
    options?: string[];
    created_by: number;
    is_built_in: boolean;
}

export interface CustomFieldValue {
    test_item_id: number;
    custom_field_definition_id: number;
    value: any;
}

export interface TestData {
    id: number;
    project_id: number;
    name: string;
    description?: string;
    data: Record<string, any>;
    attachment_ids?: number[];
    tags?: string[];
    folder_id?: number;
    created_by?: number;
    created_at?: string;
    updated_at?: string;
}

export interface Attachment {
    id: number;
    owner_type: 'test_item' | 'shared_step' | 'test_data';
    owner_id: number;
    file_name: string;
    file_url: string;
    uploaded_by: number;
    uploaded_at: string;
}

export interface TestStep {
    step_number: number;
    action: string;
    expected_result: string;
    test_data?: string;
}

export interface CheckItem {
    step_number: number;
    description: string;
    expected_result: string;
} 